import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { sileo } from "sileo";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import type {
  ApiSpecialEvent,
  ApiSpecialEventCreate,
  ApiSpecialEventUpdate,
  PaginatedResponse,
} from "@/types/api";

// Mirrors ``use-kitchen-capacity.ts`` — same React Query / ky / sileo trio.
// CRUD lives under ``/api/special-events/`` and requires ``X-Location-Id``
// (injected automatically by the staff client from the auth store).
const SPECIAL_EVENT_FIELDS = [
  "id",
  "name",
  "date",
  "end_date",
  "open_time",
  "close_time",
  "reservation_duration_minutes",
  "bypass_kitchen_cap",
  "kitchen_cap_override",
  "notes",
] as const satisfies readonly (keyof ApiSpecialEvent)[];

function showApiError(error: unknown): void {
  const data = (error as { data?: { detail?: unknown } }).data;
  const detail = data?.detail;
  if (typeof detail === "string" && detail.length > 0) {
    sileo.error({ description: detail });
    return;
  }
  sileo.error({ description: "Error de conexión. Intentá de nuevo." });
}

export function useSpecialEvents() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useQuery({
    queryKey: ["special-events", organizationId, locationId],
    queryFn: async () => {
      const params = new URLSearchParams({ fields: SPECIAL_EVENT_FIELDS.join(",") });
      const res = await api
        .get(`special-events/?${params}`)
        .json<PaginatedResponse<ApiSpecialEvent>>();
      return res.items;
    },
    enabled: isAuthenticated && !!organizationId && !!locationId,
  });
}

export function useCreateSpecialEvent() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);
  const key = ["special-events", organizationId, locationId];

  return useMutation({
    mutationFn: (data: ApiSpecialEventCreate) =>
      api.post("special-events/", { json: data }).json<ApiSpecialEvent>(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

export function useUpdateSpecialEvent() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);
  const key = ["special-events", organizationId, locationId];

  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: ApiSpecialEventUpdate }) =>
      api.patch(`special-events/${id}/`, { json: changes }).json<ApiSpecialEvent>(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

export function useDeleteSpecialEvent() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);
  const key = ["special-events", organizationId, locationId];

  return useMutation({
    mutationFn: (id: string) => api.delete(`special-events/${id}/`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
